import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { FaFacebookF, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import { accountLinks, navItems } from './data/nav';
import { faqs } from './data/faq';
import { siteConfig } from './data/config';
import { edTreatments, treatments, type Treatment } from './data/treatments';
import AssessmentFlow from './AssessmentFlow';
import './styles.css';
import './premium.css';

type ImageSlotProps = {
  src: string;
  alt: string;
  slot: string;
  className?: string;
  priority?: boolean;
  quietFallback?: boolean;
};

function ImageSlot({ src, alt, slot, className = '', priority = false, quietFallback = false }: ImageSlotProps) {
  const [failed, setFailed] = useState(false);

  return (
    <div className={'image-slot ' + className + (failed ? ' image-slot--fallback' : '')}>
      {!failed ? (
        <img
          src={src}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          onError={() => setFailed(true)}
        />
      ) : (
        <div className={'image-fallback' + (quietFallback ? ' image-fallback--quiet' : '')} role="img" aria-label={slot + ' image'}>
          {!quietFallback && <span className="sr-only">Image unavailable</span>}
        </div>
      )}
    </div>
  );
}

function Arrow({ direction = 'up' }: { direction?: 'up' | 'right' }) {
  return <span className={'arrow arrow--' + direction} aria-hidden="true">↗</span>;
}

function PromoStrip() {
  return (
    <div className="promo-strip">
      <a href={siteConfig.assessmentUrl} className="promo-strip__link">
        <span>Online care, made personal.</span>
        <span className="promo-strip__action">Start a confidential assessment <Arrow direction="right" /></span>
      </a>
    </div>
  );
}

function Header({
  planCount,
  onOpenPlan,
  cartButtonRef,
}: {
  planCount: number;
  onOpenPlan: () => void;
  cartButtonRef: RefObject<HTMLButtonElement | null>;
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenus = () => {
    setMenuOpen(false);
  };

  return (
    <header className="site-header">
      <div className="container site-header__bar">
        <a className="wordmark" href="#top" aria-label="VT.co home">VT<span>.</span>co</a>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {navItems.map((item) => (
            <a key={item.href} href={item.href}>{item.label}</a>
          ))}
        </nav>
        <div className="header-actions">
          <a className="button button--small button--dark" href={accountLinks.assessment}>Start assessment <Arrow /></a>
          <button
            className="cart-toggle"
            type="button"
            aria-label={'Open care plan' + (planCount ? `, ${planCount} item${planCount === 1 ? '' : 's'}` : '')}
            aria-controls="care-plan-drawer"
            onClick={onOpenPlan}
            ref={cartButtonRef}
          >
            <span className="cart-toggle__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" role="presentation"><path d="M4 5h2l1.4 9.1a2 2 0 0 0 2 1.7h7.7a2 2 0 0 0 1.9-1.4L21 8H7" /><circle cx="10" cy="19" r="1.25" /><circle cx="18" cy="19" r="1.25" /></svg>
            </span>
            <span className="cart-toggle__count" aria-hidden="true">{planCount}</span>
          </button>
          <button
            className="menu-toggle"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="sr-only">{menuOpen ? 'Close menu' : 'Open menu'}</span>
            <span className="menu-toggle__line" />
            <span className="menu-toggle__line" />
          </button>
        </div>
      </div>
      <div id="mobile-menu" className={'mobile-menu' + (menuOpen ? ' mobile-menu--open' : '')}>
        <div className="mobile-menu__inner">
          <div className="mobile-menu__top">
            <span>Discover care</span>
            <button type="button" onClick={closeMenus}>Close</button>
          </div>
          <nav aria-label="Mobile navigation">
            {navItems.map((item) => (
              <a key={item.href} href={item.href} onClick={closeMenus}>
                {item.label}
                <Arrow />
              </a>
            ))}
          </nav>
          <div className="mobile-menu__actions">
            <a className="button button--dark" href={accountLinks.assessment} onClick={closeMenus}>Start assessment <Arrow /></a>
          </div>
        </div>
      </div>
    </header>
  );
}

function CarePlanDrawer({
  items,
  open,
  onClose,
  onRemove,
}: {
  items: Treatment[];
  open: boolean;
  onClose: () => void;
  onRemove: (category: Treatment['category']) => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  return (
    <div className={'care-plan' + (open ? ' care-plan--open' : '')} aria-hidden={!open}>
      <button className="care-plan__backdrop" type="button" aria-label="Close care plan" onClick={onClose} tabIndex={open ? 0 : -1} />
      <aside className="care-plan__panel" id="care-plan-drawer" role="dialog" aria-modal="true" aria-labelledby="care-plan-title">
        <div className="care-plan__header">
          <div>
            <span className="eyebrow">Your next step</span>
            <h2 id="care-plan-title">Care plan</h2>
          </div>
          <button className="care-plan__close" type="button" onClick={onClose} ref={closeRef} tabIndex={open ? 0 : -1}>
            <span className="sr-only">Close care plan</span>
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {items.length ? (
          <div className="care-plan__items" aria-live="polite">
            {items.map((item) => (
              <article className="care-plan__item" key={item.category}>
                <ImageSlot src={item.image} alt="" slot={item.image.replace('/images/', '')} />
                <div className="care-plan__item-copy">
                  <span>{item.detail}</span>
                  <h3>{item.name}</h3>
                  <p>One plan per care category. Final eligibility is confirmed after assessment.</p>
                  <button type="button" className="care-plan__remove" onClick={() => onRemove(item.category)}>Remove</button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="care-plan__empty" aria-live="polite">
            <span className="care-plan__empty-mark" aria-hidden="true">+</span>
            <h3>Build your care plan.</h3>
            <p>Choose a provider-reviewed path and continue to the medical assessment when you are ready.</p>
          </div>
        )}

        <div className="care-plan__footer">
          <div className="care-plan__summary">
            <span>{items.length ? 'Assessment status' : 'No care paths selected'}</span>
            <strong>{items.length ? 'Price confirmed after assessment' : 'Choose a care path to begin'}</strong>
          </div>
          <a className={'button button--dark care-plan__continue' + (!items.length ? ' care-plan__continue--disabled' : '')} href={siteConfig.assessmentUrl} aria-disabled={!items.length} onClick={(event) => { if (!items.length) event.preventDefault(); onClose(); }}>
            Continue to medical assessment <Arrow />
          </a>
          <p className="care-plan__disclaimer">Prescription treatment requires evaluation by a licensed provider. Eligibility is not guaranteed. No payment is collected here.</p>
        </div>
      </aside>
    </div>
  );
}

function CampaignTile({
  href,
  className,
  slot,
  alt,
  label,
  title,
  description,
  priority = false,
}: {
  href: string;
  className: string;
  slot: string;
  alt: string;
  label: string;
  title: string;
  description: string;
  priority?: boolean;
}) {
  return (
    <a className={'campaign-tile ' + className} href={href}>
      <ImageSlot src={'/images/' + slot} alt={alt} slot={slot} priority={priority} />
      <div className="campaign-tile__shade" />
      <div className="campaign-tile__body">
        <span className="campaign-tile__label">{label}</span>
        <h2>{title}</h2>
        <p>{description}</p>
        <span className="campaign-tile__link">Explore care <Arrow /></span>
      </div>
    </a>
  );
}

function HeroMosaic() {
  const benefits = [
    'Licensed provider review',
    '100% online process',
    'Discreet delivery when prescribed',
    'Ongoing care-team support',
  ];

  return (
    <section className="hero" id="top">
      <div className="container hero__intro">
        <div className="hero__headline">
          <span className="eyebrow">Personalized online care</span>
          <h1>Feel like yourself again.</h1>
          <div className="hero__actions">
            <a className="button button--dark" href={'tel:' + siteConfig.contactPhone} aria-label={'Call ' + siteConfig.contactPhoneDisplay}>Call now <Arrow /></a>
            <a className="text-link" href="#how-it-works">See how it works <Arrow /></a>
          </div>
        </div>
        <div className="hero__intro-side">
          <p>Private, provider-guided care for weight loss and sexual health—built to fit real life.</p>
          <ul className="hero__trust-list" aria-label="Why choose VT.co">
            {benefits.map((benefit) => <li key={benefit}><span aria-hidden="true">✓</span>{benefit}</li>)}
          </ul>
        </div>
      </div>
      <div className="container campaign-mosaic" aria-label="Featured VT.co care paths">
        <CampaignTile
          href="#weight-loss"
          className="campaign-tile--feature"
          slot="hero-weight-loss.jpg"
          alt="Adult walking outside in soft morning light"
          label="Weight Loss"
          title="Weight loss care, online."
          description="Provider-reviewed options for eligible adults."
          priority
        />
        <CampaignTile
          href="#ed"
          className="campaign-tile--secondary"
          slot="hero-ed.jpg"
          alt="Two adults sharing a relaxed moment at home"
          label="Sexual Health"
          title="Private care for sexual health."
          description="Online support when treatment is appropriate."
          priority
        />
      </div>
    </section>
  );
}

function ProductCard({
  treatment,
  index,
  anchorId,
  selected,
  onAdd,
}: {
  treatment: Treatment;
  index: number;
  anchorId?: string;
  selected: boolean;
  onAdd: (treatment: Treatment) => void;
}) {
  return (
    <article id={anchorId} className={'product-card product-card--' + treatment.tone + (selected ? ' product-card--selected' : '')} style={{ '--card-index': index } as CSSProperties}>
      <div className="product-card__media">
        <ImageSlot src={treatment.image} alt={treatment.alt} slot={treatment.image.replace('/images/', '')} />
        <span className="product-card__badge">Rx / provider-reviewed</span>
      </div>
      <div className="product-card__body">
        <div className="product-card__meta">
          <span>{treatment.eyebrow}</span>
          <span>{treatment.detail}</span>
        </div>
        <h3>{treatment.name}</h3>
        <p>{treatment.description}</p>
        <ul className="product-card__included" aria-label="Included in this care plan">
          {treatment.included.map((item) => <li key={item}>{item}</li>)}
        </ul>
        <span className="product-card__price">Price confirmed after assessment</span>
        <div className="product-card__actions">
          <button className="button button--dark button--small" type="button" aria-pressed={selected} onClick={() => onAdd(treatment)}>
            {selected ? 'In care plan' : 'Add to care plan'} <Arrow />
          </button>
          <a className="text-link" href="#faq">Safety information <Arrow /></a>
        </div>
      </div>
    </article>
  );
}

function CareCatalog({
  planItems,
  onAdd,
}: {
  planItems: Treatment[];
  onAdd: (treatment: Treatment) => void;
}) {
  const items = [...treatments, ...edTreatments];

  return (
    <section className="product-section section" id="treatments" aria-labelledby="treatments-title">
      <div className="container product-section__head">
        <div>
          <span className="eyebrow">Explore care</span>
          <h2 id="treatments-title">Options for the goals that matter.</h2>
        </div>
        <div className="product-section__aside">
          <p>Start with a short assessment. A licensed provider reviews your health history and determines whether treatment is appropriate.</p>
        </div>
      </div>
      <div className="container care-catalog" aria-label="Treatment pathways">
        {items.map((treatment, index) => (
          <ProductCard
            treatment={treatment}
            index={index}
            anchorId={index === 0 ? 'weight-loss' : index === treatments.length ? 'ed' : undefined}
            key={treatment.name}
            selected={planItems.some((item) => item.name === treatment.name)}
            onAdd={onAdd}
          />
        ))}
        <div className="catalog-note">
          <span>Not sure where to begin?</span>
          <p>Tell us what you want to work on. Your provider can help make the next step clearer.</p>
          <a className="text-link" href={siteConfig.assessmentUrl}>Start assessment <Arrow /></a>
        </div>
      </div>
      <p className="container treatment-disclosure"><strong>Important:</strong> Prescription treatment requires evaluation by a licensed provider. Eligibility is not guaranteed. Availability and results vary. Cash-pay care.</p>
    </section>
  );
}

function AmbientVideo({
  src,
  poster,
  className,
  label,
}: {
  src: string;
  poster: string;
  className: string;
  label: string;
}) {
  const [reducedMotion, setReducedMotion] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (reducedMotion) {
      video.pause();
      return;
    }
    void video.play().catch(() => {
      // Muted autoplay can still be paused by a browser's background-tab policy.
    });
  }, [reducedMotion]);

  return (
    <video
      ref={videoRef}
      className={className}
      aria-label={label}
      autoPlay={!reducedMotion}
      controls={false}
      loop
      muted
      playsInline
      poster={poster}
      preload="metadata"
    >
      <source src={src} type="video/mp4" />
    </video>
  );
}

function OnlineCare() {
  const features = [
    ['01', 'Tell us what you want to change', 'Complete a short online assessment about your goals and health history.'],
    ['02', 'A provider reviews your information', 'A licensed provider evaluates whether treatment is appropriate for you.'],
    ['03', 'Get a plan if appropriate', 'If prescribed, your provider explains treatment and next steps.'],
    ['04', 'Stay connected online', 'Message your provider and keep care moving from home.'],
  ];

  return (
    <section className="online-section section" id="how-it-works" aria-labelledby="online-title">
      <div className="container online-section__grid">
        <div className="online-section__copy">
          <span className="eyebrow">How it works</span>
          <h2 id="online-title">Care that fits into your day.</h2>
          <p>Start with an assessment, connect with a licensed provider, and keep everything in one place.</p>
          <a className="button button--dark" href={siteConfig.assessmentUrl}>Get started <Arrow /></a>
        </div>
        <div className="online-section__media">
          <AmbientVideo
            className="care-video"
            src="/media/care-online.mp4"
            poster="/images/care-video-poster.jpg"
            label="A person using their phone at home"
          />
          <div className="online-section__media-note">
            <span>On your time</span>
            <strong>Private care from wherever you feel comfortable.</strong>
          </div>
        </div>
      </div>
      <div className="container online-features">
        {features.map(([number, title, description]) => (
          <article className="online-feature" key={number}>
            <span>{number}</span>
            <h3>{title}</h3>
            <p>{description}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

const memberStories = [
  { slot: 'member-stock-01.jpg', alt: 'Adult in soft natural window light', title: 'A clearer place to begin.', quote: 'The online process gave me a private way to organize my questions and understand what could come next.' },
  { slot: 'member-02.jpg', alt: 'Adult outside in a calm neighborhood', title: 'Care that fits the day.', quote: 'I could review everything from home and move forward at a pace that felt comfortable.' },
  { slot: 'member-stock-02.jpg', alt: 'Smiling adult relaxing at home', title: 'Support that stays close.', quote: 'Having one place for questions and follow-up made the whole experience feel more connected.' },
];

function ProofSection() {
  return (
    <section className="proof-section section" id="why-vt" aria-labelledby="proof-title">
      <div className="container proof-section__head">
        <div>
          <span className="eyebrow">A more human experience</span>
          <h2 id="proof-title">Care can feel easier.</h2>
        </div>
        <div>
          <p>These illustrative demo stories show the kind of clarity, privacy, and support VT.co is designed to provide.</p>
        </div>
      </div>
      <div className="container member-rail" id="member-rail" aria-label="Illustrative member stories">
        {memberStories.map((story, index) => (
          <article className="story-card" key={story.slot} style={{ '--card-index': index } as CSSProperties}>
            <ImageSlot src={'/images/' + story.slot} alt={story.alt} slot={story.slot} />
            <div className="story-card__body">
              <span className="story-card__draft">Member story</span>
              <h3>{story.title}</h3>
              <p>{story.quote}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ExpertsSection() {
  return (
    <section className="experts-section section" aria-labelledby="experts-title">
      <div className="container experts-section__grid">
        <div className="experts-section__copy">
          <span className="eyebrow">Clinical oversight</span>
          <h2 id="experts-title">Your health history matters here.</h2>
          <p>A licensed provider reviews your assessment, goals, and relevant health information before deciding whether prescription treatment is appropriate.</p>
          <div className="experts-section__facts">
            <span>Provider-reviewed</span>
            <span>Eligibility required</span>
            <span>Follow-up available online</span>
          </div>
          <a className="text-link" href="#faq">Read common questions <Arrow /></a>
        </div>
        <div className="experts-section__media">
          <ImageSlot src="/images/provider-consult.jpg" alt="A healthcare professional speaking with an adult during a consultation" slot="provider-consult.jpg" />
          <span>Illustrative stock image. Providers pictured are not affiliated with VT.co.</span>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="faq-section section" id="faq" aria-labelledby="faq-title">
      <div className="container faq-section__grid">
        <div className="faq-section__head" data-reveal>
          <span className="eyebrow">Before you start</span>
          <h2 id="faq-title">Questions before care.</h2>
          <p>Read the essentials before you begin an assessment.</p>
        </div>
        <div className="faq-list" data-reveal>
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div className={'faq-item' + (isOpen ? ' faq-item--open' : '')} key={faq.question}>
                <h3>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={'faq-answer-' + index}
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                  >
                    <span>{faq.question}</span>
                    <span className="faq-item__icon" aria-hidden="true">{isOpen ? '−' : '+'}</span>
                  </button>
                </h3>
                <div id={'faq-answer-' + index} className="faq-item__answer" role="region" hidden={!isOpen}>
                  <p>{faq.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="final-cta" aria-labelledby="final-title">
      <AmbientVideo
        className="final-cta__video"
        src="/media/clouds.mp4"
        poster="/images/clouds-poster.jpg"
        label="Clouds moving slowly across a blue sky"
      />
      <div className="final-cta__shade" />
      <div className="container final-cta__copy">
          <span className="eyebrow">Start online</span>
          <h2 id="final-title">A clearer next step starts here.</h2>
          <p>Complete an online assessment and learn what a licensed provider may recommend.</p>
          <a className="button button--dark" href={siteConfig.assessmentUrl}>Start assessment <Arrow /></a>
          <small>Cash-pay care. Evaluation required. Eligibility is not guaranteed.</small>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="container site-footer__top">
        <div className="site-footer__brand">
          <a className="wordmark wordmark--light" href="#top">VT<span>.</span>co</a>
          <p>Online care for the goals that matter to you.</p>
          <a className="footer-email" href={'mailto:' + siteConfig.contactEmail}>{siteConfig.contactEmail}</a>
          <div className="footer-socials" aria-label="Social channels">
            <span role="img" aria-label="Instagram coming soon" title="Instagram coming soon"><FaInstagram aria-hidden="true" /></span>
            <span role="img" aria-label="Facebook coming soon" title="Facebook coming soon"><FaFacebookF aria-hidden="true" /></span>
            <span role="img" aria-label="LinkedIn coming soon" title="LinkedIn coming soon"><FaLinkedinIn aria-hidden="true" /></span>
          </div>
        </div>
        <div className="footer-nav">
          <div>
            <h2>Explore</h2>
            <a href="#weight-loss">Weight Loss</a>
            <a href="#ed">Sexual Health</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#treatments">Treatments</a>
          </div>
          <div>
            <h2>Learn</h2>
            <a href="#faq">FAQ</a>
            <a href="#experts-title">Provider review</a>
            <a href="#why-vt">Why VT.co</a>
            <a href={siteConfig.assessmentUrl}>Start assessment</a>
          </div>
          <div>
            <h2>Support</h2>
            <a href={'mailto:' + siteConfig.contactEmail}>Contact VT.co</a>
            <span>Support questions welcome.</span>
          </div>
          <div>
            <h2>Legal</h2>
            <a href="#faq">Safety information</a>
            <a href="#faq">Privacy</a>
            <a href="#faq">Terms</a>
          </div>
        </div>
      </div>
      <div className="container site-footer__bottom">
        <p>© {new Date().getFullYear()} VT.co. For informational purposes only.</p>
        <p>Prescription treatment requires evaluation. If you have an emergency, call 911.</p>
      </div>
    </footer>
  );
}

export default function App() {
  const [planItems, setPlanItems] = useState<Treatment[]>([]);
  const [planOpen, setPlanOpen] = useState(false);
  const [planHydrated, setPlanHydrated] = useState(false);
  const [assessmentOpen, setAssessmentOpen] = useState(() => window.location.pathname.replace(/\/$/, '') === '/assessment' || window.location.hash === '#assessment');
  const cartButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const syncRoute = () => {
      if (window.location.hash === '#assessment') {
        window.history.replaceState(null, '', '/assessment');
      }
      setAssessmentOpen(window.location.pathname.replace(/\/$/, '') === '/assessment');
    };

    syncRoute();
    window.addEventListener('hashchange', syncRoute);
    window.addEventListener('popstate', syncRoute);
    return () => {
      window.removeEventListener('hashchange', syncRoute);
      window.removeEventListener('popstate', syncRoute);
    };
  }, []);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem('vt-care-plan');
      if (saved) setPlanItems(JSON.parse(saved) as Treatment[]);
    } catch {
      // A care plan is optional; fall back to an empty plan if storage is unavailable.
    } finally {
      setPlanHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!planHydrated) return;
    window.localStorage.setItem('vt-care-plan', JSON.stringify(planItems));
  }, [planHydrated, planItems]);

  const handleClosePlan = () => {
    setPlanOpen(false);
    window.setTimeout(() => cartButtonRef.current?.focus(), 0);
  };

  const handleAddToPlan = (treatment: Treatment) => {
    setPlanItems((current) => {
      const withoutCategory = current.filter((item) => item.category !== treatment.category);
      return [...withoutCategory, treatment];
    });
    setPlanOpen(true);
  };

  const handleRemoveFromPlan = (category: Treatment['category']) => {
    setPlanItems((current) => current.filter((item) => item.category !== category));
  };

  const handleExitAssessment = () => {
    setAssessmentOpen(false);
    window.history.pushState(null, '', '/');
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  if (assessmentOpen) {
    return <AssessmentFlow onExit={handleExitAssessment} />;
  }

  return (
    <div className="site-shell">
      <PromoStrip />
      <Header planCount={planItems.length} onOpenPlan={() => setPlanOpen(true)} cartButtonRef={cartButtonRef} />
      <CarePlanDrawer items={planItems} open={planOpen} onClose={handleClosePlan} onRemove={handleRemoveFromPlan} />
      <main>
        <HeroMosaic />
        <section className="benefit-bar" aria-label="VT.co care benefits">
          <div className="container benefit-bar__grid">
            <span>100% online</span>
            <span>Licensed provider review</span>
            <span>Discreet when prescribed</span>
            <span>Ongoing check-ins</span>
          </div>
        </section>
        <CareCatalog
          planItems={planItems}
          onAdd={handleAddToPlan}
        />
        <OnlineCare />
        <ProofSection />
        <ExpertsSection />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
